// LogRocket DOM masking for the L&D deposit app (LP-2622), as in lending's logrocket-pii-selectors.ts.
// The existing `data-test-id` attribute (used by QA automation) is the masking tag; there is no parallel attribute.
// Page, field, category and PII status for each id are in docs/logrocket-pii-inventory.md (the PII field inventory for
// QA). When a PII field is added to a template, give it a data-test-id and add it here and there (the keyword backstop
// below also catches most names).
//
// - LOGROCKET_PII_TEST_IDS / _PATTERNS: masked with everything inside them (displayed values, selects, answers).
// - LOGROCKET_HIDE_TEST_IDS: blanked entirely (frames showing documents or card fields).
// - LOGROCKET_VISIBLE_*: reviewed and deliberately left unmasked, and they win over every masking rule: current
//   employment, income and housing, citizenship status, business details, offers and decision, additional-question
//   answers, agreements and documents, messages, sponsor search and work phone.
// - Form values (every input, select and textarea) are masked separately unless visible, so shared field components and
//   form sections are not listed: masking their host would also blank the field labels.
//
// Patterns are [prefix, suffix] pairs for ids that carry an index or a parent component's testId; an empty string means
// "any". Fragments match anywhere in the id.

/** Exact data-test-id values masked with everything inside them. */
export const LOGROCKET_PII_TEST_IDS: readonly string[] = [
    // Repeated per row (one id for every row of the list)
    'payment.external-account-option',
    'payment.external-account-single-option',
    'payment.internal-account-option',
    'personal-information.prefilled-item-value',
    // Flow 1 - About you - membership eligibility
    'flow1-applicant.membership-eligibility-input',
    'flow1-applicant.family-member-relationship-input',
    'flow1-applicant.family-member-first-name-input',
    'flow1-applicant.family-member-last-name-input',
    'flow1-applicant.family-member-number-input',
    'flow1-applicant.financial-card-number-input',
    // Flow 1 - About you - applicant
    'flow1-applicant.first-name-input',
    'flow1-applicant.middle-name-input',
    'flow1-applicant.last-name-input',
    'flow1-applicant.email-input',
    'flow1-applicant.unit-input',
    'flow1-applicant.military-status-input',
    'flow1-applicant.trident-id-input',
    // Flow 1 - About you - business information
    'flow1-company.legal-name-input',
    'flow1-company.ein-input',
    'flow1-company.sole-proprietor-ssn-input',
    'flow1-company.formation-state-input',
    // Flow 1 - Co-applicant information
    'flow1-co-applicant.first-name-input',
    'flow1-co-applicant.middle-name-input',
    'flow1-co-applicant.last-name-input',
    'flow1-co-applicant.email-input',
    'flow1-co-applicant.military-status-input',
    // Flow 1 - Return to application (re-entry)
    'flow1-reentry.last-name-input',
    'flow1-reentry.email-input',
    // Flow 1 - Existing member verification dialog
    'flow1-existing-member.first-name-input',
    'flow1-existing-member.last-name-input',
    'flow1-existing-member.email-input',
    'flow1-existing-member.zip-code-input',
    'flow1-existing-member.last4-ssn-input',
    'flow1-existing-member.member-number-input',
    // Flow 1 - OTP verification dialog
    'flow1-otp-modal.phone-value',
    'flow1-otp-modal.otp-input',
    // Flow 1 - OTP verification
    'flow1-otp.phone-value',
    'flow1-otp.otp-input',
    // Flow 1 - Thank you / decision
    'flow1-thank-you.username-value',
    'flow1-thank-you.password-value',
    // Flow 2 - Address (flow2-address component, not used in any template right now)
    'flow2-address.full-address-input',
    // Flow 2 - Identity Verification - OTP (flow2-otp)
    'otp-verification.phone-value',
    'otp-verification.otp-input-wrapper',
    // Flow 2 - Additional Information
    'flow2-custom-questions.declarations-applicant-name-value',
    // Flow 2 - Identity Verification
    'id-verification-form.first-name-input',
    'id-verification-form.last-name-input',
    'id-verification-form.email-input',
    'id-verification-form.zip-code-input',
    'id-verification-form.last4ssn-input',
    'id-verification-form.member-number-input',
    'id-verification-form.new-member-last4ssn-input',
    // Flow 2 - Personal Information
    'personal-information.first-name-input',
    'personal-information.middle-name-input',
    'personal-information.last-name-input',
    'personal-information.unit-input',
    'personal-information.email-input',
    // Flow 2 - Joint Owner Information
    'joint-owner.first-name-input',
    'joint-owner.middle-name-input',
    'joint-owner.last-name-input',
    'joint-owner.email-input',
    // Flow 2 - Co-applicant Personal Information
    'co-applicant.first-name-input',
    'co-applicant.middle-name-input',
    'co-applicant.last-name-input',
    'co-applicant.email-input',
    'co-applicant.unit-input',
    'co-applicant.military-status-select',
    // Flow 2 - Welcome Back / Find My Application
    'reentry.last-name-input',
    'reentry.email-input',
    // Flow 2 - Thank You / Application Status
    'thank-you.username-value',
    'thank-you.password-value',
    // Error page (/error)
    'errors.user-error-value',
    'errors.page-url-value',
    'errors.error-id-value',
    'errors.back-url-value',
    'errors.route-params-value',
    // Edit Address modal
    'edit-address.country-select',
    'edit-address.street-address-input',
    'edit-address.unit-input',
    'edit-address.city-input',
    'edit-address.zip-input',
    'edit-address.state-text-input',
    'edit-address.state-select',
    'edit-address.county-input',
    // Sign Up For Online Banking
    'create-banking-account.username-input',
    'create-banking-account.username-error-value',
    'create-banking-account.password-input',
    // ID Verification (IDPal)
    'provision-idpal.description',
    // Funding - ACH modal
    'ach-modal.bank-name-input',
    'ach-modal.account-number-input',
    'ach-modal.routing-number-input',
    'ach-modal.name-on-account-input',
    'ach-modal.account-type-select',
    'ach-modal.payment-amount-input',
    // Funding - Authorize.net card modal
    'authorize-net-modal.payment-token-input',
    // Funding - Initial Deposit (payment method step)
    'payment.selected-amount-value',
    // Funding - Initial Deposit (amount per account)
    'payment.total-deposit-amount-value',
    // Funding - Internal transfer
    'payment.internal-account-select',
    // Funding - External account (Plaid, multiple)
    'payment.external-account-select',
    // Funding - External account (Plaid, single)
    'payment.external-account-single-select',
    // Funding - External account (manual)
    'payment.routing-number-input',
    'payment.bank-name-input',
    'payment.account-number-input',
    'payment.account-type-select',
    // Funding - Account details step
    'payment.deposit-amount-input',
    // Thank You (SCU download)
    'provision-thank-you.name-value',
    'provision-thank-you.member-id-value',
    'provision-thank-you.savings-account-number-value',
    'provision-thank-you.account-number-value',
    // Review - Add Authorized User
    'authorized-user.first-name-input',
    'authorized-user.middle-name-input',
    'authorized-user.last-name-input',
    'authorized-user.email-input',
    'authorized-user.mothers-maiden-name-input',
    // Review - Additional Information
    'review.phone-pin-input',
];

/** [prefix, suffix] pairs masked with everything inside them, e.g. ['review.beneficiary.first-name-input.', ''] for indexed ids. */
export const LOGROCKET_PII_TEST_ID_PATTERNS: readonly [string, string][] = [
    // Flow 1 - Co-applicant information - address history
    ['flow1-co-applicant.residence-status-input.', ''],
    // Flow 2 - Beneficiary
    ['beneficiary-page.relationship-select.', ''],
    ['beneficiary-page.first-name-input.', ''],
    ['beneficiary-page.middle-name-input.', ''],
    ['beneficiary-page.last-name-input.', ''],
    ['beneficiary-page.unit-input.', ''],
    // Flow 2 - Co-applicant Personal Information
    ['co-applicant.residence-status-select.', ''],
    // Flow 2 - Eligibility - membership options
    ['eligibility.radio.option-relation-dropdown.', ''],
    ['eligibility.radio.option-relation-input.', ''],
    ['eligibility.radio.option-family-member-first-name.', ''],
    ['eligibility.radio.option-family-member-last-name.', ''],
    ['eligibility.radio.option-member-number-input.', ''],
    ['eligibility.radio.option-student-id-input.', ''],
    ['eligibility.radio.option-sponsor-member-number-input.', ''],
    // Review - Business Details
    ['', '.unit-input'],
    // Review - Beneficial Owners
    ['review.additional-owners.first-name-input.', ''],
    ['review.additional-owners.last-name-input.', ''],
    ['review.additional-owners.company-legal-name-input.', ''],
    ['review.additional-owners.percentage-input.', ''],
    ['review.additional-owners.bo-first-name-input.', ''],
    ['review.additional-owners.bo-last-name-input.', ''],
    ['review.additional-owners.bo-percentage-input.', ''],
    // Entity name, ownership % and business name inside the beneficial-owner sentences (only the value is wrapped)
    ['review.additional-owners.entity-name-value.', ''],
    ['review.additional-owners.ownership-percentage-value.', ''],
    ['review.additional-owners.business-name-value.', ''],
    ['additional-owners.entity-name-value.', ''],
    ['additional-owners.ownership-percentage-value.', ''],
    ['additional-owners.business-name-value.', ''],
    // Review - Controlling Manager
    ['', '.manager-type-select'],
    ['review.controlling-manager.manager-type-option.', ''],
    ['', '.first-name-input'],
    ['', '.last-name-input'],
    // Reusable phone field (intl tel input)
    ['', '.country-selector-code'],
    // Documents Needed
    ['upload-documents.document-title-value.', ''],
    // Review - Additional Information; Co-applicant (Flow 1 & Flow 2)
    ['', '.mothers-maiden-name-input'],
    ['', '.last4ssn-input'],
    // Review - Beneficiary
    ['review.beneficiary.relationship-select.', ''],
    ['review.beneficiary.first-name-input.', ''],
    ['review.beneficiary.middle-name-input.', ''],
    ['review.beneficiary.last-name-input.', ''],
    ['review.beneficiary.email-input.', ''],
    ['review.beneficiary.percentage-input.', ''],
    // Review - Additional Questions (Declarations)
    ['', '.declaration-applicant-name-value'],
    // Review - Identity Information (primary & authorized user)
    ['', '.id-type-select'],
    ['', '.passport-country-select'],
    ['', '.state-id-issuer-select'],
    ['', '.military-id-issuer-input'],
    ['', '.dl-state-select'],
    ['', '.dl-number-input'],
    ['', '.id-number-input'],
    // Review - Reference
    ['', '.phone-input'],
    ['', '.email-input'],
    // Review - Address History (primary & authorized user)
    ['review.residence-info.residence-status-select.', ''],
];

/**
 * Safety net, as in lending: any data-test-id containing one of these fragments is masked, so a PII field added later
 * with a conventional name is covered before it is listed above. Lending's list without its vehicle ids (deposit has
 * no vehicles) and without `employer` / `income`, which deposit shows (see LOGROCKET_VISIBLE_*), plus the deposit
 * ID-document and credential names. The lists above always win: PII ids and patterns mask even where this list skips
 * an element, and visible fields are skipped here.
 */
export const LOGROCKET_PII_TEST_ID_KEYWORDS: readonly string[] = [
    'ssn', 'first-name', 'middle-name', 'last-name', 'full-name', 'maiden', 'email', 'phone', 'dob', 'birth', 'address',
    'street', 'zip', 'county', 'city', 'unit-input', 'account-number', 'routing', 'tax-id', 'ein-input', 'credit-score',
    'otp-input', 'otp-box', 'otp-code', 'signature', 'member-number', 'license', 'id-number', 'dl-number', 'dl-state',
    'passport', 'password', 'username',
];

/**
 * data-test-id endings the keyword safety net skips: labels, hints, validation messages, links, buttons, headings,
 * wrappers and checkboxes (e.g. "Same as mailing address"). The same endings followed by `.<index>`
 * (e.g. `first-name-label.0`) are skipped too. Masking these would blank labels and static text.
 */
export const LOGROCKET_KEYWORD_EXCLUDED_SUFFIXES: readonly string[] = [
    '-label', '.label', '-hint', '.hint', '-error', '-link', '-btn', '-button', '-title', '.title', '.form', '-form',
    '-section', '.section', '-container', '-wrapper', '-component', '.component', '-checkbox',
];

/**
 * Shared field components and form sections render their labels inside the host, so the safety net skips the host;
 * the values inside are masked as form values.
 */
export const LOGROCKET_FIELD_COMPONENT_TAGS: readonly string[] = [
    'creditsnap-address', 'm-edit-address', 'creditsnap-date', 'creditsnap-phone-number', 'mat-intl-tel-input',
    'creditsnap-ssn', 'creditsnap-yearmonth', 'creditsnap-radio', 'flow2-ssn', 'flow2-date', 'flow2-otp', 'flow2-address',
    'cs-additional-info', 'cs-identity-info', 'cs-residence-info', 'cs-employment-info', 'cs-beneficiary',
    'cs-reference-applicant', 'creditsnap-custom-questions', 'creditsnap-additional-owners',
    'creditsnap-additional-information', 'creditsnap-controlling-manager',
];

/** Elements the safety net skips: field components, plus buttons, steps, cards, forms and form-field chrome. */
export const LOGROCKET_KEYWORD_EXCLUDED_TAGS: readonly string[] = [
    ...LOGROCKET_FIELD_COMPONENT_TAGS, 'button', 'mat-step', 'savvy-card', 'form', 'mat-form-field', 'mat-label', 'mat-hint',
    'mat-error',
];

/**
 * PII rendered by third-party code (Angular Material, Google Places) where a data-test-id cannot be placed: the open
 * date picker, select/autocomplete panels, tooltips naming owners, and address suggestions. (The selected value of
 * each mat-select is masked with the other form values.)
 */
export const LOGROCKET_THIRD_PARTY_REDACT_SELECTORS: readonly string[] = [
    '.mat-datepicker-content',
    '.mat-mdc-select-panel',
    '.mat-mdc-autocomplete-panel',
    '.mat-mdc-tooltip',
    '.pac-container',
];

/** Exact data-test-id values hidden from replay entirely. */
export const LOGROCKET_HIDE_TEST_IDS: readonly string[] = [
    // ID Verification (IDPal)
    'provision-idpal.iframe',
    // Funding - Authorize.net card modal
    'authorize-net-modal.payment-iframe',
    // Funding - Repay card modal
    'repay-modal.payment-iframe',
];

/** Exact data-test-id values left unmasked on request, including the inputs inside them. */
export const LOGROCKET_VISIBLE_TEST_IDS: readonly string[] = [
    // Funding - Initial Deposit: amount entered for each account (same id on every row)
    'payment.account-deposit-amount-input',
    // Flow 1 - Return to application (re-entry): product and application start date of each matching application
    'flow1-reentry.application-option-value',
    // Flow 1 - About you - membership eligibility
    'flow1-applicant.sponsor-organization-search-input',
    'flow1-applicant.sponsor-organization-name-input',
    // Flow 1 - About you - applicant
    'flow1-applicant.business-title-input',
    'flow1-applicant.citizenship-input',
    'flow1-applicant.error-message-value',
    // Flow 1 - About you - business information
    'flow1-company.entity-type-input',
    'flow1-company.employee-count-input',
    'flow1-company.annual-revenue-input',
    // Duration in business (host only; its inputs keep the shared year-month-input ids)
    'flow1-company.business-length',
    // Flow 1 - Co-applicant information
    'flow1-co-applicant.citizenship-input',
    'flow1-co-applicant.error-message-value',
    // Flow 1 - Select products - offer cards
    'flow1-offer-card.error-message-value',
    // Flow 1 - Select products - 'Show more' dialog
    'flow1-product-offer-info.product-terms-value',
    // Flow 1 - SSN verification dialog
    'flow1-ask-ssn.error-message-value',
    // Flow 1 - Existing member verification dialog
    'flow1-existing-member.error-message-value',
    // Flow 1 - Loan purpose dialog
    'flow1-purpose-code-modal.other-purpose-input',
    // Flow 2 - Review Agreement
    'agreements.final-consent-document-value',
    'agreements.print-iframe',
    // Flow 2 - Personal Information (Income)
    'add-income.gross-monthly-income-input',
    'add-income.monthly-housing-costs-input',
    'add-income.occupation-input',
    // Flow 2 - Personal Information
    'personal-information.citizenship-select',
    'personal-information.error-message',
    // Flow 2 - Joint Owner Information
    'joint-owner.error-message',
    // Flow 2 - Co-applicant Personal Information
    'co-applicant.citizenship-select',
    'co-applicant.error-message',
    'co-applicant.form-error-message',
    // Alert dialog (global)
    'message-model.message-value',
    // Alert dialog (global, loading)
    'message-model.loading-message-value',
    // Consent for Final Submission modal
    'deposit-consent-modal.consent-html',
    'deposit-consent-modal.print-iframe',
    // Unsubscribe
    'unsubscribe.reason-input',
    'unsubscribe.error-message-value',
    // Snackbar notification (global)
    'snackbar.message-value',
    // E-Sign (DocuSign)
    'provision-esign.docusign-iframe',
    // Review - Add Authorized User
    'authorized-user.citizenship-select',
    // Review - Additional Information
    'review.gross-annual-income-input',
    'review.rent-mortgage-input',
    // Error and alert messages
    'errors.message-value',
    'confirm-dialog.description-value',
    'create-banking-account.password-error-value',
];

/** [prefix, suffix] pairs for visible ids that carry an index (same format as LOGROCKET_PII_TEST_ID_PATTERNS). */
export const LOGROCKET_VISIBLE_TEST_ID_PATTERNS: readonly [string, string][] = [
    // Flow 2 - Welcome Back / Find My Application: product and application start date of each matching application
    ['reentry.application-option-label.', ''],
    // Flow 1 - Co-applicant information - employment history
    ['flow1-co-applicant.employment-type-input.', ''],
    // Flow 1 - Select products - offer cards
    ['flow1-offer-card.product-terms-value.', ''],
    // Flow 2 - Additional Information
    ['flow2-custom-questions.select-answer-input.', ''],
    ['flow2-custom-questions.radio-answer-input.', ''],
    ['flow2-custom-questions.textarea-answer-input.', ''],
    ['flow2-custom-questions.text-answer-input.', ''],
    ['flow2-custom-questions.declaration-answer-input.', ''],
    ['flow2-custom-questions.declaration-sub-answer-input.', ''],
    ['flow2-custom-questions.asset-amount-input.', ''],
    // Flow 2 - Co-applicant Personal Information
    ['co-applicant.employment-type-select.', ''],
    ['co-applicant.employment-length.', ''],
    // Review - Business Details
    ['', '.website-input'],
    ['', '.business-type-input'],
    ['', '.business-description-input'],
    // Review - Beneficial Owners
    ['review.additional-owners.title-input.', ''],
    ['review.additional-owners.bo-title-input.', ''],
    // Review - Controlling Manager
    ['', '.title-input'],
    // Cross-sell offers
    ['cross-sell-offer-card.loan-amount-value.', ''],
    ['cross-sell-offer-card.apr-value.', ''],
    ['cross-sell-offer-card.monthly-savings-value.', ''],
    // Decision - eligible products
    ['provision-decision.product-value.', ''],
    // Documents Needed
    ['upload-documents.file-name-button.', ''],
    ['upload-documents.file-input.', ''],
    ['upload-documents.file-name-value.', ''],
    // Review - Employment History (primary & authorized user): type and length on every row, as in lending
    ['review.employment-info.employment-type-select.', ''],
    ['review.employment-info.employment-length.', ''],
    ['authorized-user.employment-info.employment-type-select.', ''],
    ['authorized-user.employment-info.employment-length.', ''],
];

/** Fragments left unmasked wherever they appear in an id (shared components rendered under several parents). */
export const LOGROCKET_VISIBLE_TEST_ID_FRAGMENTS: readonly string[] = [
    // Review - Additional Information; Co-applicant (Flow 1 & Flow 2)
    'work-phone',
    // Employer name and job title on every page and every row (current and prior employments)
    'employer-name-input',
    'job-title-input',
    // Membership eligibility employer / organization search, under every radio-group prefix
    '.option-search-input.',
    '.option-search-results.',
    '.option-search-result-item.',
    // Review - Additional Questions
    '-custom-questions.select-input.',
    '-custom-questions.textarea-input.',
    '-custom-questions.text-input.',
    '-custom-questions.number-input.',
    '-custom-questions.radio-group.',
    '-custom-questions.checkbox-input.',
    '-custom-questions.multi-checkbox-option.',
    '-custom-questions.sub-select-input.',
    '-custom-questions.sub-textarea-input.',
    '-custom-questions.sub-text-input.',
    '-custom-questions.sub-number-only-input.',
    '-custom-questions.sub-number-input.',
    '-custom-questions.sub-radio-group.',
    '-custom-questions.sub-checkbox-input.',
    '-custom-questions.sub-multi-checkbox-option.',
    '-custom-questions.grand-sub-select-input.',
    '-custom-questions.grand-sub-textarea-input.',
    '-custom-questions.grand-sub-text-input.',
    '-custom-questions.grand-sub-number-input.',
    '-custom-questions.grand-sub-radio-group.',
    '-custom-questions.grand-sub-checkbox-input.',
    '-custom-questions.grand-sub-multi-checkbox-option.',
    // Review - Additional Questions (Declarations, assets and liabilities)
    '-custom-questions.declaration-radio-group.',
    '-custom-questions.declaration-sub-select-input.',
    '-custom-questions.assets-input.',
];

/** Elements that hold a typed or selected value, including the text a mat-select shows for its value. */
export const LOGROCKET_FORM_VALUE_ELEMENTS: readonly string[] = ['input', 'textarea', 'select', '.mat-mdc-select-value-text'];

// operator: '=' (exact), '^=' (prefix), '$=' (suffix), '*=' (contains)
function attributeSelector(operator: string, value: string): string {
    return `[data-test-id${operator}"${value.replace(/["\\]/g, '\\$&')}"]`;
}

/** [prefix, suffix] -> `[data-test-id^="prefix"][data-test-id$="suffix"]`; an empty part matches anything. */
function patternSelector([prefix, suffix]: readonly [string, string]): string {
    return (prefix ? attributeSelector('^=', prefix) : '') + (suffix ? attributeSelector('$=', suffix) : '');
}

function unique(selectors: string[]): string[] {
    return Array.from(new Set(selectors));
}

/** Visible fields as [id text, selector], so the masking patterns and keyword safety net can skip them. */
function visibleFields(): [string, string][] {
    return [
        ...LOGROCKET_VISIBLE_TEST_IDS.map((id): [string, string] => [id, attributeSelector('=', id)]),
        ...LOGROCKET_VISIBLE_TEST_ID_PATTERNS.map((pattern): [string, string] => [pattern.join(''), patternSelector(pattern)]),
        ...LOGROCKET_VISIBLE_TEST_ID_FRAGMENTS.map((fragment): [string, string] => [fragment, attributeSelector('*=', fragment)]),
    ];
}

/**
 * Masking pattern that skips visible fields it could also match (as in lending, visible wins), e.g. the generic
 * `*.country-selector-code` pattern must not mask the work phone's dial code. Only overlapping visible fields are
 * appended, which keeps each selector short.
 */
function maskPatternSelector(pattern: readonly [string, string]): string {
    const [prefix, suffix] = pattern;
    const matches = (id: string) => id.startsWith(prefix) && id.endsWith(suffix);
    const overlapping = [
        ...LOGROCKET_VISIBLE_TEST_IDS.filter(matches).map(id => attributeSelector('=', id)),
        ...LOGROCKET_VISIBLE_TEST_ID_PATTERNS
            .filter(([p]) => p.startsWith(prefix) || (prefix.startsWith(p) && p !== ''))
            .map(patternSelector),
        ...LOGROCKET_VISIBLE_TEST_ID_FRAGMENTS.map(fragment => attributeSelector('*=', fragment)),
    ];
    return patternSelector(pattern) + unique(overlapping).map(s => `:not(${s})`).join('');
}

/**
 * All selectors for `dom.redactSelectors`: form values, exact ids, prefix/suffix patterns, keyword backstop and
 * third-party DOM. Every form value is masked unless it is a visible field (this replaces inputSanitizer, which cannot
 * exempt fields), so shared field components and form sections need no entry and their labels stay visible.
 */
export function buildLogRocketRedactSelectors(): string[] {
    const visible = visibleFields();
    const visibleSelectors = unique(visible.map(([, selector]) => selector));
    const notVisible = visibleSelectors.length ? `:not(${visibleSelectors.map(s => `${s}, ${s} *`).join(', ')})` : '';
    // Labels, hints, errors, links, buttons, headings and wrappers (also with a trailing `.<index>`), and field components.
    const keywordExclusions = [
        ...LOGROCKET_KEYWORD_EXCLUDED_SUFFIXES.map(suffix =>
            `:not(${attributeSelector('$=', suffix)}):not(${attributeSelector('*=', suffix + '.')})`),
        ...LOGROCKET_KEYWORD_EXCLUDED_TAGS.map(tag => `:not(${tag})`),
    ].join('');
    // Each keyword also skips visible fields whose id contains it (e.g. work-phone).
    const keywordSelector = (keyword: string) => attributeSelector('*=', keyword) + keywordExclusions + visible
        .filter(([id]) => id.includes(keyword))
        .map(([, selector]) => `:not(${selector}):not(${selector} *)`)
        .join('');
    return unique([
        ...LOGROCKET_FORM_VALUE_ELEMENTS.map(element => element + notVisible),
        ...LOGROCKET_PII_TEST_IDS.map(id => attributeSelector('=', id)),
        ...LOGROCKET_PII_TEST_ID_PATTERNS.map(maskPatternSelector),
        ...LOGROCKET_PII_TEST_ID_KEYWORDS.map(keywordSelector),
        ...LOGROCKET_THIRD_PARTY_REDACT_SELECTORS,
    ]);
}

/** Elements hidden from replay entirely: cross-origin frames showing ID documents or card fields. */
export const LOGROCKET_HIDE_SELECTORS: readonly string[] = unique(LOGROCKET_HIDE_TEST_IDS.map(id => attributeSelector('=', id)));
